import { BufferTrackerReact } from '@buffer-mono/tracking-plan'

import { selectCurrentOrganizationId } from '~publish/legacy/organizations/selectors'
import store from '~publish/legacy/store'

import AppStore from '../../../stores/AppStore'
import type { ComposerProfile } from '../../../stores/types'

type FormattedProfile = {
  channelId: string
  channel: string
  channelServiceId: string
  channelUsername: string
  channelType: string
}

export type ChannelsSelectedTrackingDetails = {
  organizationId: string
  profiles: FormattedProfile[]
  sendMentionURLPastedFailed: (text: string) => void
  sendMentionURLPastedSucceeded: (text: string) => void
  sendMentionSuggestionAccepted: () => void
  sendMentionRecentSuggestionAccepted: () => void
}

function formatSelectedProfilesForTracking({
  profiles,
}: {
  profiles: ComposerProfile[]
}): FormattedProfile[] {
  return profiles.map((profile) => ({
    channelId: profile.id || '',
    channel: profile.serviceName || '',
    channelServiceId: profile.serviceId || '',
    channelUsername: profile.serviceUsername || '',
    channelType: profile.serviceType || '',
  }))
}

export function selectedChannelsForTracking({
  serviceName,
}: {
  serviceName: string
}): ChannelsSelectedTrackingDetails {
  // Get organization id
  const organizationId = selectCurrentOrganizationId(store.getState()) ?? ''

  // Get selected profiles in the composer and format them for tracking events
  const selectedProfilesInComposer =
    AppStore.getSelectedProfilesForService(serviceName)
  const formattedProfiles = formatSelectedProfilesForTracking({
    profiles: selectedProfilesInComposer,
  })

  return {
    organizationId,
    profiles: formattedProfiles,
    sendMentionURLPastedFailed: (text: string): void => {
      // Send a tracking event for every profile
      formattedProfiles.forEach((profile) => {
        BufferTrackerReact.mentionURLPastedFailed({
          organizationId,
          channelId: profile.channelId,
          channelServiceId: profile.channelServiceId,
          channelType: profile.channelType,
          channelUsername: profile.channelUsername,
          channel: profile.channel,
          clientName: 'publishWeb',
          url: text,
        })
      })
    },
    sendMentionURLPastedSucceeded: (text: string): void => {
      // Send a tracking event for every profile
      formattedProfiles.forEach((profile) => {
        BufferTrackerReact.mentionURLPastedSucceeded({
          organizationId,
          channelId: profile.channelId,
          channelServiceId: profile.channelServiceId,
          channelType: profile.channelType,
          channelUsername: profile.channelUsername,
          channel: profile.channel,
          clientName: 'publishWeb',
          url: text,
        })
      })
    },
    sendMentionSuggestionAccepted: (): void => {
      // Send a tracking event for every profile
      formattedProfiles.forEach((profile) => {
        BufferTrackerReact.mentionSuggestionAccepted({
          organizationId,
          channelId: profile.channelId,
          channelServiceId: profile.channelServiceId,
          channelType: profile.channelType,
          channelUsername: profile.channelUsername,
          channel: profile.channel,
          clientName: 'publishWeb',
        })
      })
    },
    sendMentionRecentSuggestionAccepted: (): void => {
      // Send a tracking event for every profile
      formattedProfiles.forEach((profile) => {
        BufferTrackerReact.mentionRecentSuggestionAccepted({
          organizationId,
          channelId: profile.channelId,
          channelServiceId: profile.channelServiceId,
          channelType: profile.channelType,
          channelUsername: profile.channelUsername,
          channel: profile.channel,
          clientName: 'publishWeb',
        })
      })
    },
  }
}
